import * as React from 'react';

import { DocumentRequest } from '../models';
import { applicationSettings } from '../utils';

type Action = { type: 'updateDocumentRequest'; payload: DocumentRequest } | { type: 'updateRequestId'; payload: string } | { type: 'updateIsLoading'; payload: boolean } | { type: 'updateError'; payload: string } | { type: 'updateIsCompleted' };
type Dispatch = (action: Action) => void;
type State = {
    requestId: string | null;
    documentRequest: DocumentRequest;
    isLoading: boolean;
    error: string | null;
    isCompleted: boolean;
};
type CountProviderProps = { children: React.ReactNode };

const DocumentUploadContext = React.createContext<{
    state: State;
    dispatch: Dispatch;
} | null>(null);

const DocumentUploadReducer: React.Reducer<State, Action> = (state: State, action: Action) => {
    switch (action.type) {
        case 'updateDocumentRequest': {
            return {
                ...state,
                documentRequest: action.payload,
                error: null,
                isLoading: false,
                isCompleted: action.payload.isCompleted,
            };
        }
        case 'updateRequestId': {
            return { ...state, requestId: action.payload, error: null };
        }
        case 'updateIsLoading': {
            return { ...state, isLoading: action.payload };
        }
        case 'updateError': {
            return { ...state, error: action.payload, isLoading: false };
        }
        case 'updateIsCompleted': {
            return {
                ...state,
                documentRequest: initialDocumentRequestState(),
                isCompleted: true,
            };
        }
        default: {
            throw new Error(`Unhandled action type`);
        }
    }
};

const initialState = (): State => {
    return {
        requestId: null,
        documentRequest: initialDocumentRequestState(),
        isLoading: true,
        error: null,
        isCompleted: false,
    } as State;
};

const initialDocumentRequestState = (): DocumentRequest => {
    return {
        id: '',
        heading: null,
        subHeading: null,
        isCompleted: false,
        branding: {
            clientId: applicationSettings.clientId,
            brandId: applicationSettings.brandId,
        },
    } as DocumentRequest;
};

const DocumentUploadProvider = ({ children }: CountProviderProps) => {
    const [state, dispatch] = React.useReducer(DocumentUploadReducer, initialState());
    const value = { state, dispatch };
    return <DocumentUploadContext.Provider value={value}>{children}</DocumentUploadContext.Provider>;
};

const useDocumentUpload = () => {
    const context = React.useContext(DocumentUploadContext);
    if (context === null) {
        throw new Error('useDocumentUpload must be used within a DocumentUploadProvider');
    }
    return context;
};

export { DocumentUploadProvider, useDocumentUpload };
